import { motion } from "framer-motion";
import { formatCurrency } from "../../../data/mock/dashboardData";

const COLORS = ["#B91C1C", "#DC2626", "#E5B80B", "#c9a209", "#347a62", "#0369a1"];

const R = 54;
const C = 2 * Math.PI * R;
const GAP = 3;

export default function PortfolioMixChart({ items }) {
  const source = Array.isArray(items) && items.length
    ? items.map((row) => ({
        label: row.name || row.label || "Batch",
        amount: Number(row.outstanding ?? row.amount ?? row.total ?? 0),
      }))
    : [];

  const total = source.reduce((sum, s) => sum + s.amount, 0);
  const segmentsInput = source.map((seg, i) => ({
    ...seg,
    value: total > 0 ? Math.round((seg.amount / total) * 100) : 0,
    color: COLORS[i % COLORS.length],
  }));

  if (!segmentsInput.length || total <= 0) {
    return (
      <p className="flex min-h-[180px] flex-1 items-center justify-center text-sm text-primary/40">
        No portfolio mix data yet.
      </p>
    );
  }

  let offset = 0;
  const segments = segmentsInput.map((seg) => {
    const length = (seg.value / 100) * C - GAP;
    const dashOffset = -offset;
    offset += (seg.value / 100) * C;
    return { ...seg, length: Math.max(length, 0), dashOffset };
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-hidden">
      <div className="relative shrink-0">
        <svg
          className="h-44 w-44 sm:h-48 sm:w-48"
          viewBox="0 0 140 140"
          role="img"
          aria-label="Portfolio mix by batch"
        >
          <circle
            cx="70"
            cy="70"
            r={R}
            fill="none"
            stroke="rgba(127,29,29,0.06)"
            strokeWidth="14"
          />
          {segments.map((seg, i) => (
            <motion.circle
              key={seg.label}
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={seg.color}
              strokeWidth="14"
              strokeLinecap="butt"
              strokeDasharray={`${seg.length} ${C}`}
              strokeDashoffset={seg.dashOffset}
              transform="rotate(-90 70 70)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.08 + i * 0.06 }}
            />
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-[10px] font-medium uppercase tracking-wide text-primary/40">Outstanding</p>
          <p className="text-sm font-bold text-primary">{formatCurrency(total)}</p>
        </div>
      </div>

      <ul className="w-full space-y-1.5">
        {segmentsInput.slice(0, 4).map((seg) => (
          <li key={seg.label} className="flex items-center justify-between gap-2 text-[11px]">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: seg.color }} />
              <span className="truncate text-primary/65">{seg.label}</span>
            </span>
            <span className="shrink-0 font-semibold text-primary">
              {seg.value}% · {formatCurrency(seg.amount)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
